import { EMPTY_COMMENTS_DATA } from '@/constants/comment'
import { RECOMMEND_VARIANT, RecommType } from '@/constants/recommend'
import { getCmsBaseURL } from '@/lib/configs'
import { env } from '@/lib/runtime-env'
import { shuffle } from '@/lib/shuffle'
import { parseSocialLinks } from '@/schemas/media'
import { Locale } from '@/server/constants/locales'
import { setPasscodeCookie } from '@/server/lib/passcode'
import { getUnifiedBIRecommendation } from '@/server/services/bi'
import {
  API_ACCOUNTS_V3,
  API_ACCOUNTS_V4,
  API_ADD_ROOM_UPLOAD_ASSET,
  API_ENDPOINT_COMMENTS_COUNT,
  API_ENDPOINT_COMMENTS_V2,
  API_ENDPOINT_USER_TASKS,
  API_GET_ACTIVITY_SHOWCASE,
  API_GET_CREATOR_TUTORIAL,
  API_GET_EDITOR_CHOICES,
  API_GET_EVENTS,
  API_GET_FOLLOWING_ROOMS,
  API_GET_HUBS,
  API_GET_LIKED_ROOMS_V3,
  API_GET_ME_SAVED_WORLDS,
  API_GET_ME_V4,
  API_GET_MY_GIVEAWAY_LIST,
  API_GET_MY_ROOMS_V3,
  API_GET_RECOMMEND_CREATORS,
  API_GET_RECOMMEND_WORLDS,
  API_GET_ROOM_META_DATA,
  API_GET_ROOM_POSTMESSAGE_ORIGINS,
  API_GET_ROOM_PREFERRED_DEVICES,
  API_GET_ROOM_SEARCH_FACETS,
  API_GET_ROOM_SETTINGS,
  API_GET_ROOM_TAGS,
  API_GET_ROOM_TAGS_V2,
  API_GET_ROOMS_AUTOCOMPLETE,
  API_GET_ROOMS_EXPLORE,
  API_GET_ROOMS_V1,
  API_GET_SCENES,
  API_GET_SEARCH_ACCOUNTS,
  API_GET_STANDALONE_CONTENT,
  API_GET_THEMES,
  API_GET_VIDEO_STATE,
  API_GET_VOUCHER_ROOMS_V2,
  API_ME_RECENT_WATCHED_ROOMS,
  API_ME_WATCH_HISTORY_ROOMS,
  API_MY_ROOMS_V1,
  API_MY_ROOMS_V2,
  API_POST_ACCOUNTS_SEARCH_V2,
  API_POST_ROOMS_SEARCH,
  API_RECENT_SEARCH_V1,
  API_ROOMS_V2,
  API_ROOMS_V3,
} from '@/services/api'
import { PRIV_API_ACCOUNTS_V4 } from '@/services/priv-api'
import { CommentsData } from '@/types/comments'
import { DeviceTypeWithAndroidIOS, EntryPass, VendorType } from '@/types/configs'
import { RoomTag, RoomTagWithCount } from '@/types/genre'
import { PostMessageOriginsResponse } from '@/types/room'
import { RoomStatus } from '@/types/room-status'
import type {
  RoomMetaBlendvisionScene,
  RoomMetaPlaycanvasScene,
  RoomMetaSceneGroup,
  RoomMetaSpokeScene,
  RoomMetaStandaloneScene,
} from '@/types/scene'
import { WorldTypeValue } from '@/types/search'
import { BannerMeta, Media, SearchSortOption, SocialLink } from '@/types/store'
import { WatchHistoryResponse } from '@/types/watch-history'
import { ImageAsset, PolicyValue, VideoAsset } from '@/types/world'
import { z } from 'zod'

// Basic types
export type PaginationPages = {
  total: number
  start: number
  limit: number
  prev: string
  next: string
}

export type PaginatedData<T> = {
  results: T[]
  total: number
}

export type Host = {
  id?: string
  display_name?: string
}

export type Speaker = {
  id?: string
  display_name?: string
  description?: string
  image?: string
}

export type Owner = {
  id: string
  display_name: string
  snapshot_url: string
  htc_account_id: string
  head_icon_url: string
  community_user_id?: string
  viverse_pro?: boolean
  verified_creator?: boolean
}

type BaseRoom = {
  id: string
  title: string
  description: string
  description_plaintext: string
  /** Creator rich-text description in CommonMark (CONNECT-6046); absent or blank falls back to `description_plaintext`. */
  description_markdown?: string | null
  image: string
  hub_sid: string
  hubs_url: string
  scene_sid: string
  policy: PolicyValue
  view_count: number
  feed_view_count?: number
  like_count: number
  is_protected: boolean
  is_opened: boolean
  is_voucher_room: boolean
  is_selected?: boolean
  is_exclusive?: boolean
  is_hot?: boolean
  is_top?: boolean
  is_live?: boolean
  is_liked?: boolean
  is_saved?: boolean
  owner: Owner
  logo_overlay_position?: LogoPosition
  mobile_portrait_logo_position?: LogoPosition
  mobile_landscape_logo_position?: LogoPosition
  features?: {
    fullscreen_for_embedding?: boolean
  }
  github_url?: string
  vod_id?: string
  content_type?: RoomContentType | null
}

export type PerformanceTier = 'UNKNOWN' | 'TIER_1' | 'TIER_2' | 'TIER_3'

export type Room = BaseRoom & {
  description?: string
  is_distributed_room?: boolean
  total_vram_kb: number
  online_counts?: number
  tags?: (string | RoomDetailTag)[]
  external_link?: string
  preferred_devices: DeviceTypeWithAndroidIOS[]
  is_standalone: boolean
  is_adult_only: boolean
  is_featured?: boolean
  is_voucher_room: boolean
  vod_duration?: number
  vod_file_name?: string
  vod_id?: string
  vod_last_update_date?: string
  feed_videos?: FeedVideo[]
  trailer_videos?: TrailerVideo[]
  feed_cta_link?: string
  performance_tier: PerformanceTier
  content_type?: RoomContentType | null
  first_public_date?: string
  last_updated_at?: string
}

// Mirrors hubs-cms-go dto/room_card.go: the trimmed room returned by the v2 editor-choices and
// recommend-worlds routes.
export type RoomCardOwner = {
  display_name: string
  community_user_id?: string
  head_icon_url?: string
  viverse_pro?: boolean
  verified_creator?: boolean
}

export type RoomCard = {
  id: string
  title: string
  image: string
  hub_sid: string
  hubs_url: string
  external_link?: string
  content_type?: Pick<RoomContentType, 'code'>
  owner: RoomCardOwner | null
  is_exclusive: boolean
  is_hot: boolean
  is_live: boolean
  is_selected: boolean
  is_top: boolean
  is_voucher_room: boolean
  view_count: number
  like_count: number
  first_public_date?: string
  last_updated_at?: string
  preferred_devices?: DeviceTypeWithAndroidIOS[]
  vod_id?: string
  vod_duration?: number
  vod_last_update_date?: string
  trailer_videos?: TrailerVideo[]
  description_plaintext?: string
  feed_cta_link?: string
  feed_videos?: FeedVideo[]
  feed_view_count?: number
}

export type EditorChoiceCards = {
  id: string
  title: string
  is_pinned: boolean
  pinned_count?: number
  preferred_devices?: DeviceTypeWithAndroidIOS[]
  is_ranked: boolean
  is_premium: boolean
  is_device_filter_enabled: boolean
  rooms: RoomCard[]
}

// Recommendation rails merge BI results (full Room) with the recommend-worlds fallback (RoomCard).
export type RecommendRoom = Room | RoomCard

export type ViewCountSource = 'feed'

export const ViewCountResponseSchema = z.object({
  view_count: z.number(),
  feed_view_count: z.number().optional(),
})

export type ViewCountResponse = z.infer<typeof ViewCountResponseSchema>

export const parseViewCountResponse = (value: unknown): ViewCountResponse | null => {
  const parsed = ViewCountResponseSchema.safeParse(value)
  return parsed.success ? parsed.data : null
}

type CreatorTutorial = {
  title: string
  description: string
  image: string
  action_link: string
}

export type UserWorld = BaseRoom & {
  max_presence_capacity: number
  max_instance_capacity: number
  theme: string
  features: {
    spatial_sound: boolean
    fullscreen_for_embedding?: boolean
  }
}

export type ThemeRoom = BaseRoom & {
  theme_order: number
  is_distributed_room: boolean
  online_counts?: number
}

export type SubImage = {
  directus_files_id?: string
}

export type Video = {
  video_id?: string
}

export type HashTag = {
  id?: string
  value: string
}

export type Category = {
  id?: string
  value: string
}

export type EditorChoice = {
  id: string
  image: string
  banner_image: string
  title: string
  description: string
  is_pinned: boolean
  pinned_count?: number
  rooms: Room[]
  is_device_filter_enabled?: boolean
  preferred_devices?: DeviceTypeWithAndroidIOS[]
  is_premium?: boolean
  is_ranked?: boolean
  is_exclusive?: boolean
}

// function types
export type Event = {
  id: string
  image: string
  title: string
  description: string
  logo?: string
  agenda?: string
  start_time?: string
  end_time?: string
  is_liked?: boolean
  is_promoted?: boolean
  like_count?: number
  view_count?: number
  hosts: Host[]
  speakers: Speaker[]
  rooms: Room[]
  sub_images: SubImage[]
  videos: Video[]
  hashtags: HashTag[]
  category: Category[]
}

export type EventsResults = {
  results: Event[]
  pages: PaginationPages
}

export type ActivityShowcaseList = {
  background?: string
  video?: VideoAsset
  description: string
  description_html?: string
  title: string
  title_html?: string
  action_title: string
  action_link: string
  action_link_2?: string
  action_title_2?: string
  card?: string
  view_count?: number
  like_count?: number
  online_counts?: number
  preferred_devices?: DeviceTypeWithAndroidIOS[]
  enable_no_signup_hint?: boolean
}

export type TrailerVideo = {
  vod_id: string
  file_id: string
  cover_image?: string
}

export type FeedVideo = {
  vod_id: string
  file_id: string
  cover_image?: string
}

export type TrailerImage = {
  id?: string
  url?: string
}

export type TrailerMedia = {
  trailer_videos?: TrailerVideo[]
  trailer_images?: TrailerImage[]
}

export enum ActivityShowcaseCategory {
  Landing = 'landing',
  Explore = 'explore',
  PersonaLanding = 'persona_landing',
}

export const fetchCMSApiWithStatusCode = <Response = Record<string, unknown>>(
  url: string,
  method: RequestInit['method'] = 'GET',
  payload: RequestInit['body'],
  params: Omit<RequestInit, 'method' | 'body'> = {},
  htcToken = '',
  isSSR: boolean = false,
): Promise<{ status: number; results: Response; headers: Headers } | void> => {
  const cmsBaseURL = getCmsBaseURL(isSSR)
  const cmsUrl = `${cmsBaseURL}${url}`

  const init: RequestInit = {
    headers: {
      'content-type': 'application/json',
    },
    method,
    ...params,
  }

  if (htcToken) {
    init.headers = {
      ...init.headers,
      ...{ authkey: htcToken },
    }
  }
  if (isSSR) {
    const userAgent = env('USER_AGENT')
    if (userAgent) {
      init.headers = {
        ...init.headers,
        ...{ 'User-Agent': userAgent },
      }
    }
  }
  if (payload) {
    init.body = JSON.stringify(payload)
  }
  return fetch(cmsUrl, init)
    .then(async (response) => {
      const result = await response.text()
      try {
        return { status: response.status, results: JSON.parse(result), headers: response.headers }
      } catch (e) {
        // Don't return json.
        return { status: response.status, results: result, headers: response.headers }
      }
    })
    .catch((error) => {
      console.error('fetchCMSApiWithStatusCode error:', { url, error })
    })
}

export function getEditorChoices(
  start: number = 0,
  limit: number = 0,
  category:
    | 'landing'
    | 'explore'
    | 'new'
    | 'persona_landing'
    | 'game'
    | 'playable_feed' = 'landing',
  locale?: string,
  totalVramKb?: number,
  isSSR: boolean = true,
  htcToken?: string,
) {
  const queryString = new URLSearchParams({
    start: start.toString(),
    limit: limit.toString(),
    category,
    ...(typeof totalVramKb === 'number' && { total_vram_kb: totalVramKb.toString() }),
    ...(locale && { locale }),
  }).toString()

  return fetchCMSApiWithStatusCode<PaginatedResponse<EditorChoiceCards>>(
    `${API_GET_EDITOR_CHOICES}?${queryString}`,
    'GET',
    null,
    {},
    htcToken ?? '',
    isSSR,
  )
}

export function getActivityShowcase(
  category: ActivityShowcaseCategory = ActivityShowcaseCategory.Landing,
  { start = 0, limit = 0, locale = '' },
  isSSR: boolean = true,
) {
  return fetchCMSApiWithStatusCode<PaginatedResponse<ActivityShowcaseList>>(
    `${API_GET_ACTIVITY_SHOWCASE}?start=${start}${
      locale ? `&locale=${locale}` : ''
    }&limit=${limit}&category=${category}`,
    'GET',
    null,
    {},
    '',
    isSSR,
  )
}

export function getEvents(params: Partial<EventsQueryParams> = {}, isSSR: boolean = false) {
  const url = getAppendedQueryUrl(API_GET_EVENTS, params, isSSR)
  return fetchCMSApiWithStatusCode<PaginatedResponse<Event>>(url, 'GET', null, {}, '', isSSR)
}

export function getTheme(themeId: string) {
  return fetchCMSApiWithStatusCode<Theme>(`${API_GET_THEMES}/${themeId}`, 'GET', null, {}, '', true)
}

export function postLikeRoom(roomId: string, htcToken: string) {
  return fetchCMSApiWithStatusCode<Room>(
    `${API_GET_ROOMS_V1}/${roomId}/liked`,
    'POST',
    null,
    {},
    htcToken,
  )
}

export function postUnlikeRoom(roomId: string, htcToken: string) {
  return fetchCMSApiWithStatusCode<Room>(
    `${API_GET_ROOMS_V1}/${roomId}/unliked`,
    'POST',
    null,
    {},
    htcToken,
  )
}

export function postSaveRoom(roomId: string, htcToken: string) {
  return fetchCMSApiWithStatusCode<Room>(
    `${API_GET_ROOMS_V1}/${roomId}/saved`,
    'POST',
    null,
    {},
    htcToken,
  )
}

export function postUnsaveRoom(roomId: string, htcToken: string) {
  return fetchCMSApiWithStatusCode<Room>(
    `${API_GET_ROOMS_V1}/${roomId}/unsaved`,
    'POST',
    null,
    {},
    htcToken,
  )
}

export function getRoom(roomId: string, htcToken: string) {
  return fetchCMSApiWithStatusCode<Room>(`${API_GET_ROOMS_V1}/${roomId}`, 'GET', null, {}, htcToken)
}

export type RoomsResults = {
  results: Room[]
  pages: PaginationPages
}

export type SceneType =
  | 'spoke'
  | 'playcanvas'
  | 'standalone'
  | 'blendvision'
  | 'all'
  | 'spoke,playcanvas,standalone'

export function getMyRooms(
  htcToken: string,
  start: number = 0,
  limit: number = 0,
  sceneType?: SceneType | '',
) {
  const query = new URLSearchParams({
    limit: limit.toString(),
    start: start.toString(),
    exclude_persona: 'true',
  })

  if (sceneType !== undefined) {
    query.append('scene_type', sceneType)
  }

  return fetchCMSApiWithStatusCode<PaginatedResponse<Room>>(
    `${API_MY_ROOMS_V2}?${query.toString()}`,
    'GET',
    null,
    {},
    htcToken,
  )
}

export type MyRoomsContentTypeId = 1 | 2 | 3

export const CONTENT_TYPE_CODE_TO_ID: Record<ContentTypeCode, MyRoomsContentTypeId> = {
  experiences: 1,
  games: 2,
  videos: 3,
  templates: 1,
}

export function getMyRoomsV3(
  htcToken: string,
  {
    start = 0,
    limit = 8,
    contentTypeId,
  }: { start?: number; limit?: number; contentTypeId?: MyRoomsContentTypeId },
) {
  const query = new URLSearchParams({
    limit: limit.toString(),
    start: start.toString(),
    exclude_persona: 'true',
    include: 'owner',
    scene_type: 'all',
  })

  if (contentTypeId !== undefined) {
    query.append('content_type', contentTypeId.toString())
  }

  return fetchCMSApiWithStatusCode<PaginatedResponse<Room>>(
    `${API_GET_MY_ROOMS_V3}?${query.toString()}`,
    'GET',
    null,
    {},
    htcToken,
  )
}

export function getMyPersonas(htcToken: string, start: number = 0, limit: number = 0) {
  return fetchCMSApiWithStatusCode<PaginatedResponse<Room>>(
    `${API_GET_MY_ROOMS_V3}?limit=${limit}&start=${start}&tag=Persona`,
    'GET',
    null,
    {},
    htcToken,
  )
}

export function getCoOwnedRooms(htcToken: string, isSSR: boolean = false) {
  return fetchCMSApiWithStatusCode<PaginatedResponse<Room>>(
    `${API_GET_ME_V4}/co-owned-worlds`,
    'GET',
    null,
    {},
    htcToken,
    isSSR,
  )
}

type RoomSettings = {
  devices: 'others' | 'iphone' | 'android' | 'mobile_vr'[]
  max_presence_size: number
  max_room_instance: number
  scene: {
    id: string
    scene_sid: string
    version: string
    scene_type: string
    owner: string
    scene_meta: {
      scene_url: string
      config_url: string
      game_scripts_url: string
      preview_url: string
    }
    settings: {
      disabledViverseSystem: boolean
      automaticOcclusionCulling: boolean
    }
  }
}

export function getRoomSettings(hubSid: string) {
  return fetchCMSApiWithStatusCode<RoomSettings[]>(
    `${API_GET_ROOM_SETTINGS}?hub_sid=${hubSid}`,
    'GET',
    null,
    {},
  )
}

export type UploadStatusType = 'READY' | 'PROCESSING' | 'TRANSCODING' | 'FAILED' | 'UNKNOWN'

export type UploadState = {
  status: UploadStatusType
  file_name: string
  error?: {
    code: string
    message: string
  }
}

export function getVideoUploadState(hubSid: string) {
  return fetchCMSApiWithStatusCode<UploadState>(`${API_GET_VIDEO_STATE}/${hubSid}`, 'GET', null, {})
}

type QueryParams = {
  start: number
  limit: number
  keyword: string
}

export type RoomsQueryParams = QueryParams & {
  locale: Locale
  filter: string
  account_id: string
  subject_id: string
  exclude_persona?: boolean
  nft_chain: string
  hub_sid: string
  tag: string
  total_vram_kb: number
  is_voucher_room?: boolean
  sort?: SearchSortOption
  exclude_giveaway?: boolean
  scene_type?: SceneType
  device?: DeviceTypeWithAndroidIOS
  content_types?: ContentTypeCode[]
  game_score?: number
  voob?: boolean
}

type EventsQueryParams = QueryParams & {
  locale: Locale
  filter: string
  total_vram_kb: number
  status: 'opened' | 'closed' | 'soon'
}

type SearchAccountsQueryParams = QueryParams & {}

function getAppendedQueryUrl(url: string, params: Record<string, unknown>, isSSR: boolean = false) {
  const parsedUrl = new URL(`${getCmsBaseURL(isSSR)}${url}`)

  for (const [key, value] of Object.entries(params)) {
    if (value == null) continue
    if (Array.isArray(value)) {
      parsedUrl.searchParams.append(key, value.join(','))
    } else {
      parsedUrl.searchParams.append(key, String(value))
    }
  }

  return `${parsedUrl.pathname}${parsedUrl.search}`
}

export function getPublicRoomsV3(
  params: Partial<RoomsQueryParams>,
  htcToken?: string,
  isSSR = false,
) {
  const url = getAppendedQueryUrl(API_ROOMS_V3, params, isSSR)
  return fetchCMSApiWithStatusCode<PaginatedResponse<RoomDetail>>(
    url,
    'GET',
    null,
    {},
    htcToken,
    isSSR,
  )
}

export type ExploreRoomItem = {
  id: string
  title: string
  hub_sid: string
  image: string
  view_count: number
  like_count: number
  owner_name: string
  owner_community_user_id?: string
  owner_viverse_pro?: boolean
  owner_verified_creator?: boolean
  preferred_devices?: DeviceTypeWithAndroidIOS[]
  is_liked?: boolean
  is_saved?: boolean
  is_voucher_room: boolean
  is_selected?: boolean
  is_exclusive?: boolean
  is_hot?: boolean
  is_top?: boolean
  vod_id?: string
  vod_duration?: number
  vod_last_update_date?: string
  trailer_videos?: TrailerVideo[]
  tags?: { id: number; name: string; is_promoted: boolean; description?: string }[]
  content_type?: RoomContentType | null
  category?: { id: number; name: string } | null
  custom_tags?: string[]
  game_score_points?: number
}

export type ExploreRoomsQueryParams = {
  locale?: string
  site_locale?: string
  sort?: SearchSortOption
  device?: DeviceTypeWithAndroidIOS
  tag?: string
  scene_type?: SceneType
  exclude_persona?: boolean
  exclude_giveaway?: boolean
  limit?: number
  start?: number
  content_types?: ContentTypeCode[]
  game_score?: number
}

export function exploreRooms(params: ExploreRoomsQueryParams, htcToken?: string, isSSR = false) {
  const url = getAppendedQueryUrl(
    API_GET_ROOMS_EXPLORE,
    {
      ...params,
      owner_viverse_pro: true,
      owner_verified_creator: true,
    },
    isSSR,
  )

  return fetchCMSApiWithStatusCode<PaginatedResponse<ExploreRoomItem>>(
    url,
    'GET',
    null,
    {},
    htcToken,
    isSSR,
  )
}

export function getLikedRooms(
  htcToken: string,
  params: Partial<RoomsQueryParams>,
  isSSR: boolean = false,
) {
  const url = getAppendedQueryUrl(API_GET_LIKED_ROOMS_V3, params, isSSR)
  return fetchCMSApiWithStatusCode<PaginatedResponse<Room>>(url, 'GET', null, {}, htcToken, isSSR)
}

export function getSavedRooms(
  htcToken: string,
  params: Partial<RoomsQueryParams>,
  isSSR: boolean = false,
) {
  const url = getAppendedQueryUrl(API_GET_ME_SAVED_WORLDS, params, isSSR)
  return fetchCMSApiWithStatusCode<PaginatedResponse<SavedWorldEntry>>(
    url,
    'GET',
    null,
    {},
    htcToken,
    isSSR,
  )
}

export type SavedWorldAvailable = Room & { status: typeof RoomStatus.Available }
export type SavedWorldDeleted = { hub_sid: string; status: typeof RoomStatus.Deleted }
export type SavedWorldNoAccess = {
  hub_sid: string
  status: typeof RoomStatus.NoAccess
  title: string
}
export type SavedWorldEntry = SavedWorldAvailable | SavedWorldDeleted | SavedWorldNoAccess

export function isSavedWorldAvailable(entry: SavedWorldEntry): entry is SavedWorldAvailable {
  return entry.status === RoomStatus.Available
}

export type UserProfile = {
  id: string
  wallet_address: string
  htc_account_email: string
  htc_account_phone_number: string
  display_name: string
  htc_account_id: string
  like_rooms: Room[]
  liked_events: Event[]
  oursong_account_id: string
  community_user_id: string
  is_over_18: boolean
  active_avatar: {
    snapshot_url: string
    head_icon_url: string
    gender: number
    is_half_body: string
    data_type: string
  }
  bio?: string
  profile_banner: string
  profile_banner_meta?: BannerMeta
  profile_media: Media[]
  show_social_links: boolean
  social_links?: SocialLink[]
  accept_tip?: boolean
  show_following_creators?: boolean
  viverse_pro?: boolean
  verified_creator?: boolean
}

async function fetchAccountByPreferredUsername<Response extends Partial<UserProfile>>(
  preferredUsername: string,
  htcToken = '',
  isSSR = false,
) {
  const queryParams = new URLSearchParams({
    preferred_username: preferredUsername,
  })

  const response = await fetchCMSApiWithStatusCode<Response>(
    `${PRIV_API_ACCOUNTS_V4}?${queryParams.toString()}`,
    'GET',
    null,
    {},
    htcToken,
    isSSR,
  )

  if (response?.results?.social_links !== undefined) {
    response.results.social_links = parseSocialLinks(response.results.social_links)
  }

  return response
}

export function getUserProfileByPreferredUsername(preferredUsername: string, htcToken?: string) {
  return fetchAccountByPreferredUsername<Partial<UserProfile>>(
    preferredUsername,
    htcToken || '',
    true,
  )
}

export type recommendCreatorResults = {
  results: Creator[]
  pages: PaginationPages
}

export function getRecommendCreators(url?: string, limit: number = 30) {
  return fetchCMSApiWithStatusCode<PaginatedResponse<Creator>>(
    url ?? `${API_GET_RECOMMEND_CREATORS}?limit=${limit}`,
    'GET',
    null,
    {},
    '',
    true,
  )
}

export type Creator = {
  id: string
  community_user_id: string
  display_name: string
  head_icon_url: string
  is_pinned?: boolean
  weight?: number
  viverse_pro?: boolean
  verified_creator?: boolean
}

export type Selection = {
  title: string
  scene_sid: string
  images: ImageAsset[]
  videos: VideoAsset[]
  nftData?: {
    nftVendor: NFTVendor
    externalData: any
  }
}

export type SelectedItem = {
  item: string
  redeem_time: string
}

export type Giveaway = {
  id: string
  title: string
  room_size: number
  chance: number
  type: 'scene'
  selections: Selection[]
  selected: SelectedItem[]
}

export type DigitalAssetPod = {
  chain: string
  contract: string
  contract_type: string
  token_id: string
  vendor_type: string
  token_source: {
    name: string
    description: string
    animation_url: string
    image: string
    scene_sid: string
    store_link: string
  }
  external_data: {
    product_id: string
    product_item_id: string
    product_asset_id: string
    urlKey: string
  }
}

export type DigitalAssetPodResults = {
  result: DigitalAssetPod[]
  pages: PaginationPages
}

export function getMyGiveaways(htcToken: string) {
  return fetchCMSApiWithStatusCode<PaginatedResponse<Giveaway>>(
    API_GET_MY_GIVEAWAY_LIST,
    'GET',
    null,
    {},
    htcToken,
  )
}

export function getAScene(htcToken: string, sceneSid: string) {
  return fetchCMSApiWithStatusCode<PaginatedResponse<Giveaway>>(
    `${API_GET_SCENES}/${sceneSid}`,
    'GET',
    null,
    {},
    htcToken,
  )
}

export type NFTVendor = 'generic_nft' | 'vivebytes' | 'sketchfab_id' | 'oursong'
export type LogoPosition =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right'

export type CreateRoomData = {
  scene_sid: string
  policy: Room['policy']
  title: string
  image: ImageAsset['id']
  imageUrl: string
  features: {
    spatial_sound: boolean
  }
  passcode?: string
  description_plaintext: string
  tags?: string
  max_presence_size?: number
  nft_vendor?: NFTVendor
  external_data?: any
  preferred_devices?: string[]
  is_adult_only?: boolean
  logo_overlay_position?: LogoPosition
  github_url?: string
}

export function postCreateAGiveawayRoom(
  htcToken: string,
  giveawayId: Giveaway['id'],
  data: CreateRoomData,
) {
  return fetchCMSApiWithStatusCode<Room>(
    `${API_GET_MY_GIVEAWAY_LIST}/${giveawayId}/create-room`,
    'POST',
    // @ts-ignore
    data,
    {},
    htcToken,
  )
}

export function postCreateMyRoom(htcToken: string, data: CreateRoomData) {
  return fetchCMSApiWithStatusCode<Room>(
    API_MY_ROOMS_V2,
    'POST',
    // @ts-ignore
    data,
    {},
    htcToken,
  )
}

export function getDigitalAssetPods(
  htcToken: string,
  profileId: string,
  vendorType: string,
  chainName: string,
) {
  return fetchCMSApiWithStatusCode<DigitalAssetPodResults>(
    `${API_ACCOUNTS_V3}/${profileId}/nft/${chainName}/${vendorType}/pods?curating_only=true`,
    'GET',
    null,
    {},
    htcToken,
  )
}

type UploadRoomImageResponse = {
  id: string
  url: string
}

export const fetchUploadRoomImage = async (
  htcToken: string,
  file: File,
  isSSR: boolean = false,
) => {
  const formData = new FormData()
  formData.append('asset', file)

  const params = {
    headers: {},
    body: formData,
  }

  return fetchCMSApiWithStatusCode<UploadRoomImageResponse>(
    API_ADD_ROOM_UPLOAD_ASSET,
    'POST',
    null,
    params,
    htcToken,
    isSSR,
  )
}

export function deleteRoom(htcToken: string, roomId: string) {
  return fetchCMSApiWithStatusCode<RoomsResults>(
    `${API_MY_ROOMS_V1}/${roomId}`,
    'DELETE',
    null,
    {},
    htcToken,
  )
}

export function withdrawCoOwnedRoom(htcToken: string, roomId: string, profileId: string) {
  return fetchCMSApiWithStatusCode<RoomsResults>(
    `${API_ROOMS_V3}/${roomId}/co-owners/${profileId}`,
    'DELETE',
    null,
    {},
    htcToken,
  )
}

export type EntryPassResults = {
  result: EntryPass[]
  pages: PaginationPages
}

export function getUserEntryPassByCreator(
  htcToken: string,
  profileId: string,
  creatorId: string,
  chain: string = 'digital_asset',
  vendorType: VendorType = 'vivebytes',
) {
  return fetchCMSApiWithStatusCode<EntryPassResults>(
    `${API_ACCOUNTS_V3}/${profileId}/nft/${chain}/${vendorType}/vouchers?creator_account_id=${creatorId}`,
    'GET',
    null,
    {},
    htcToken,
  )
}

export type PatchRoomData = {
  policy: Room['policy']
  title: string
  image: ImageAsset['id']
  passcode?: string
  features: {
    spatial_sound: boolean
  }
  description_plaintext: string
  max_presence_size?: number
}

export function patchRoom(htcToken: string, roomId: string, data: PatchRoomData) {
  return fetchCMSApiWithStatusCode<Room>(
    `${API_MY_ROOMS_V1}/${roomId}`,
    'PATCH',
    // @ts-ignore
    data,
    {},
    htcToken,
  )
}

export type Tag = {
  name: string
}
export type PaginatedResponse<T> = { results: T[]; pages: PaginationPages }

export function getRoomTags(isSSR = true, type?: string) {
  const url = type ? `${API_GET_ROOM_TAGS}?type=${type}` : API_GET_ROOM_TAGS
  return fetchCMSApiWithStatusCode<PaginatedResponse<Tag>>(url, 'GET', null, {}, '', isSSR)
}

type RoomTagsV2Body = {
  results: {
    content_type: { id: number; name: string; tags: RoomTag[] }[]
  }
}

export async function getVideoRoomTags(params: { locale?: string } = {}, isSSR = true) {
  const url = getAppendedQueryUrl(
    API_GET_ROOM_TAGS_V2,
    { ...params, content_type: 'videos' },
    isSSR,
  )

  const res = await fetchCMSApiWithStatusCode<RoomTagsV2Body>(url, 'GET', null, {}, '', isSSR)
  return res?.results?.results?.content_type?.flatMap((c) => c.tags) ?? []
}

export async function getGameRoomTags(params: { locale?: string } = {}, isSSR = true) {
  const url = getAppendedQueryUrl(API_GET_ROOM_TAGS_V2, { ...params, content_type: 'games' }, isSSR)

  const res = await fetchCMSApiWithStatusCode<RoomTagsV2Body>(url, 'GET', null, {}, '', isSSR)
  return res?.results?.results?.content_type?.flatMap((c) => c.tags) ?? []
}

export function getRoomPreferredDevices(isSSR = true) {
  return fetchCMSApiWithStatusCode<PaginatedResponse<Tag>>(
    API_GET_ROOM_PREFERRED_DEVICES,
    'GET',
    null,
    {},
    '',
    isSSR,
  )
}

export type Theme = {
  id: string
  title: string
  description: string
  image: string
  banner_image: string
  policy: string
  hosted: string
  logo: string
  rooms: ThemeRoom[]
}

export function getThemes() {
  return fetchCMSApiWithStatusCode<PaginatedResponse<Theme>>(
    API_GET_THEMES,
    'GET',
    null,
    {},
    '',
    true,
  )
}

export function getFollowingRooms(
  htcToken: string,
  start: number = 0,
  limit: number = 0,
  totalVramKb: number,
) {
  return fetchCMSApiWithStatusCode<PaginatedResponse<Room>>(
    `${API_GET_FOLLOWING_ROOMS}?limit=${limit}&start=${start}&total_vram_kb=${totalVramKb}`,
    'GET',
    null,
    {},
    htcToken,
  )
}

export function getGuestAccount(htcToken: string, communityUserId: string, isSSR: boolean = false) {
  return fetchAccountByPreferredUsername<UserProfile>(communityUserId, htcToken, isSSR)
}

export type SearchAccount = {
  id: string
  community_user_id: string
  display_name: string
  head_icon_url: string
  viverse_pro?: boolean
  verified_creator?: boolean
}

export function getSearchAccounts(
  params: Partial<SearchAccountsQueryParams> = {},
  isSSR: boolean = false,
) {
  const url = getAppendedQueryUrl(API_GET_SEARCH_ACCOUNTS, params, isSSR)
  return fetchCMSApiWithStatusCode<PaginatedResponse<SearchAccount>>(
    url,
    'GET',
    null,
    {},
    '',
    isSSR,
  )
}

export type RecommendWorldsCategory = DeviceTypeWithAndroidIOS | 'video'

export function getRecommendWorlds(
  htcToken: string | undefined,
  {
    limit = 0,
    locale,
    device,
    random = false,
    content_types = ['experiences', 'games', 'videos', 'templates'],
  }: {
    limit?: number
    locale?: string
    device: RecommendWorldsCategory
    random?: boolean
    content_types?: ContentTypeCode[]
  },
  isSSR = false,
) {
  const url = getAppendedQueryUrl(
    API_GET_RECOMMEND_WORLDS,
    {
      limit,
      ...(locale && { locale }),
      ...(device && { device: `recommend_${device}` }),
      random,
      content_types,
    },
    isSSR,
  )

  return fetchCMSApiWithStatusCode<EditorChoiceCards>(url, 'GET', null, {}, htcToken, isSSR)
}

export function getRandomRecommendWorlds(
  htcToken: string | undefined,
  {
    device,
    limit = 0,
    content_types,
  }: {
    device: RecommendWorldsCategory
    limit?: number
    content_types?: ContentTypeCode[]
  },
  isSSR = false,
) {
  return getRecommendWorlds(htcToken, { device, limit, random: true, content_types }, isSSR).then(
    (res) => res?.results.rooms,
  )
}

export function getRecommendWorldsByRecommType(
  {
    recommType,
    worldId,
    ua,
    limit,
  }: {
    recommType: RecommType
    worldId?: string
    ua: string
    limit: number
  },
  htcToken?: string,
  cookieHeader?: string,
  csdataBid?: string,
) {
  return getUnifiedBIRecommendation({
    recommType,
    ...(worldId && { worldId }),
    ua,
    limit,
    htcToken,
    cookieHeader,
    csdataBid,
  })
}

export function fetchRandomRecommendRooms({
  htcToken,
  device,
  limit,
  excludeWorldId,
  contentTypes,
  isSSR = false,
}: {
  htcToken?: string
  device: RecommendWorldsCategory
  limit: number
  excludeWorldId?: string
  contentTypes?: ContentTypeCode[]
  isSSR?: boolean
}) {
  const requestLimit = excludeWorldId ? limit + 1 : limit

  return getRandomRecommendWorlds(
    htcToken,
    { device, limit: requestLimit, content_types: contentTypes },
    isSSR,
  ).then((rooms) => {
    const fallbackRooms = rooms ?? []

    if (!excludeWorldId) return fallbackRooms

    return fallbackRooms.filter((room) => room.hub_sid !== excludeWorldId).slice(0, limit)
  })
}

export function fetchRecommendRooms({
  recommType,
  device,
  limit,
  worldId,
  ua,
  htcToken,
  cookieHeader,
  csdataBid,
  contentTypes,
  isSSR = false,
}: {
  recommType: string
  device: DeviceTypeWithAndroidIOS
  limit: number
  worldId?: string
  ua: string
  htcToken?: string
  cookieHeader?: string
  csdataBid?: string
  contentTypes?: ContentTypeCode[]
  isSSR?: boolean
}) {
  const fallback = () =>
    fetchRandomRecommendRooms({
      htcToken,
      device,
      limit,
      excludeWorldId: worldId,
      contentTypes,
      isSSR,
    })

  const shuffleResult = (rooms: RecommendRoom[]) => shuffle(rooms)

  if (recommType === RECOMMEND_VARIANT.CONTROL) {
    return fallback().then(shuffleResult)
  }

  // NOTE: Keep the recommendation rail populated when personalized results are unavailable.
  return getRecommendWorldsByRecommType(
    {
      recommType: recommType as RecommType,
      worldId,
      ua,
      limit,
    },
    htcToken,
    cookieHeader,
    csdataBid,
  )
    .then((rooms): RecommendRoom[] | Promise<RecommendRoom[]> =>
      rooms && rooms.length > 0 ? rooms : fallback(),
    )
    .then(shuffleResult)
    .catch(() => fallback().then(shuffleResult))
}

export function getWorldsYouMayLike(
  htcToken: string | undefined,
  limit: number = 10,
  ua: string,
  cookieHeader?: string,
  csdataBid?: string,
) {
  return getRecommendWorldsByRecommType(
    { recommType: 'personalized-v2', ua, limit },
    htcToken,
    cookieHeader,
    csdataBid,
  )
    .then((rooms) => shuffle(rooms ?? []))
    .catch((error) => {
      console.error('getWorldsYouMayLike error: 🔥', error)
      return []
    })
}

export function postFeaturedRoom(htcToken: string, roomId: string) {
  return fetchCMSApiWithStatusCode<Room>(
    `${API_MY_ROOMS_V2}/${roomId}/featured`,
    'POST',
    null,
    {},
    htcToken,
  )
}

export function deleteFeaturedRoom(htcToken: string, roomId: string) {
  return fetchCMSApiWithStatusCode<Room>(
    `${API_MY_ROOMS_V2}/${roomId}/featured`,
    'DELETE',
    null,
    {},
    htcToken,
  )
}

export function getFeaturedRooms(
  params: Partial<RoomsQueryParams>,
  htcToken?: string,
  cmsAccountId?: string,
  isSSR = false,
) {
  const url = getAppendedQueryUrl(
    `${API_ACCOUNTS_V4}/${cmsAccountId}/featured-rooms`,
    params,
    isSSR,
  )

  return fetchCMSApiWithStatusCode<PaginatedResponse<Room>>(url, 'GET', null, {}, htcToken, isSSR)
}

export function getRoomTagRecommendWorlds(
  tag: string,
  params: Partial<RoomsQueryParams & { type: WorldTypeValue }>,
  htcToken?: string,
  isSSR = false,
) {
  const encodedTag = encodeURIComponent(tag)

  const url = getAppendedQueryUrl(
    `${API_GET_ROOM_TAGS}/${encodedTag}/recommend-worlds`,
    params,
    isSSR,
  )

  return fetchCMSApiWithStatusCode<PaginatedResponse<Room>>(url, 'GET', null, {}, htcToken, isSSR)
}

export function getCreatorTutorial(locale: string, isSSR = true) {
  const queryString = new URLSearchParams({
    ...(locale && { locale }),
  }).toString()

  return fetchCMSApiWithStatusCode<PaginatedResponse<CreatorTutorial>>(
    `${API_GET_CREATOR_TUTORIAL}?${queryString}`,
    'GET',
    null,
    {},
    '',
    isSSR,
  )
}

export type StandaloneContentResult = {
  publish_url?: string
}

export function getStandaloneContent(htcToken: string, sceneSid: string) {
  return fetchCMSApiWithStatusCode<StandaloneContentResult>(
    `${API_GET_STANDALONE_CONTENT}/${sceneSid}`,
    'GET',
    null,
    {},
    htcToken,
  )
}

export type RoomDetailTag = {
  id: number
  name: string
  is_promoted: boolean
}

export type ContentTypeCode = 'experiences' | 'games' | 'videos' | 'templates'

export type RoomContentType = {
  id: number
  name: string
  code: ContentTypeCode
  description?: string | null
  image?: string
}

export type RoomCategory = {
  id: number
  name: string
  code: string
}

export type RoomDetail = Omit<Room, 'tags'> & {
  tags?: RoomDetailTag[]
  create_time?: string
  last_updated_at?: string
  first_public_date?: string
  is_saved?: boolean
  content_type?: RoomContentType | null
  category?: RoomCategory | null
  custom_tags?: string[]
}

export type RoomPageDataBase = RoomDetail & {
  availability_required: boolean
  preview_granted: boolean
}

/** The detailed metadata endpoint returns information about a room and its scene data. */
export type RoomPageData =
  | (RoomPageDataBase & {
      scene_type: 'playcanvas'
      scene: RoomMetaPlaycanvasScene
    })
  | (RoomPageDataBase & {
      scene_type: 'standalone'
      scene: RoomMetaStandaloneScene
    })
  | (RoomPageDataBase & {
      scene_type: 'blendvision'
      scene: RoomMetaBlendvisionScene
    })
  | (RoomPageDataBase & {
      scene_type: 'spoke_scene'
      scene: RoomMetaSpokeScene
      /** Present only for Spoke worlds. */
      model_url?: string
    })
  | (RoomPageDataBase & {
      scene_type: 'pc_scene_group'
      scene: RoomMetaSceneGroup
    })

/** Fetches the unified payload for the room page, combining two former APIs into one. */
export const getRoomPageData = async (
  hubSid: string,
  htcToken?: string,
  isSSR = true,
  locale?: string,
  version?: string,
) => {
  const url = getAppendedQueryUrl(
    `${API_GET_ROOM_META_DATA}/${hubSid}/meta`,
    { locale, version },
    isSSR,
  )

  const response = await fetchCMSApiWithStatusCode<RoomPageData>(
    url,
    'GET',
    null,
    {},
    htcToken,
    isSSR,
  )

  if (response?.status !== 200) return undefined
  return response.results
}

export type CoOwner = {
  id: string
  display_name: string
  head_icon_url?: string
}

export const getRoomCoOwners = async (roomId: string, htcToken?: string, isSSR = true) => {
  return fetchCMSApiWithStatusCode<PaginatedResponse<CoOwner>>(
    `${API_ROOMS_V3}/${roomId}/co-owners`,
    'GET',
    null,
    {},
    htcToken,
    isSSR,
  )
}

export const getCoOwnersByRoomId = async (roomData: RoomDetail | undefined) => {
  const response = await getRoomCoOwners(roomData!.id)
  return response?.results?.results
}

export function getCoOwners(htcToken: string | undefined, roomId: string) {
  return fetchCMSApiWithStatusCode<PaginatedResponse<Owner>>(
    `${API_ROOMS_V3}/${roomId}/co-owners`,
    'GET',
    null,
    {},
    htcToken,
  )
}

export function updateRoomCoOwners(
  htcToken: string | undefined,
  roomId: string,
  coOwnersList: string[],
  isSSR = false,
) {
  return fetchCMSApiWithStatusCode<PaginatedResponse<CoOwner>>(
    `${API_ROOMS_V3}/${roomId}/co-owners`,
    'PATCH',
    // @ts-expect-error
    { co_owners: coOwnersList },
    {},
    htcToken,
    isSSR,
  )
}

export const fetchTrailerMedia = (roomId: string, htcToken?: string) => {
  return fetchCMSApiWithStatusCode<TrailerMedia>(
    `${API_ROOMS_V2}/${roomId}/trailer-media`,
    'GET',
    null,
    {},
    htcToken,
  )
}

export const updateTrailerMedia = (
  htcToken: string,
  roomId: string,
  trailerMedia: TrailerMedia,
) => {
  return fetchCMSApiWithStatusCode<TrailerMedia>(
    `${API_ROOMS_V2}/${roomId}/trailer-media`,
    'PATCH',
    // @ts-ignore
    trailerMedia,
    {},
    htcToken,
  )
}

export const getWorldViewCountEndpoint = (worldId: string, source?: ViewCountSource) => {
  const endpoint = `${API_GET_HUBS}/${worldId}/viewed`
  return source ? `${endpoint}?source=${source}` : endpoint
}

export const addWorldViewCount = (worldId: string, source?: ViewCountSource) => {
  return fetchCMSApiWithStatusCode<ViewCountResponse>(
    getWorldViewCountEndpoint(worldId, source),
    'POST',
    null,
    {},
  )
}

export async function validatePasscode(
  passcode: string,
  roomId: string,
  htcAuthKey = '',
): Promise<{ isPass: boolean; suffix?: string }> {
  const response = await fetchCMSApiWithStatusCode(
    `/api/hubs-cms/v1/passcode/${roomId}`,
    'POST',
    // @ts-ignore
    { passcode },
    {},
    htcAuthKey,
    true,
  )

  if (response?.status !== 200) return { isPass: false }

  const cookieSuffix = await setPasscodeCookie(roomId, passcode)
  return { isPass: true, suffix: cookieSuffix }
}

export const getComments = async ({
  roomId,
  parentId,
  limit = 50,
  cursor = '',
  htcAuthKey = '',
  isSSR = false,
}: {
  roomId: string
  parentId?: string
  limit?: number
  cursor?: string
  htcAuthKey?: string
  isSSR?: boolean
}) => {
  const queryParams = new URLSearchParams({
    room_id: roomId,
    ...(parentId && { parent_id: parentId }),
    limit: limit.toString(),
    cursor: cursor,
  })

  const response = await fetchCMSApiWithStatusCode<CommentsData>(
    `${API_ENDPOINT_COMMENTS_V2}?${queryParams.toString()}`,
    'GET',
    null,
    {},
    htcAuthKey,
    isSSR,
  )

  if (response?.status !== 200) return EMPTY_COMMENTS_DATA

  return response.results as CommentsData
}

export const getCommentsCount = async ({
  roomId,
  isSSR = false,
}: {
  roomId: string
  isSSR?: boolean
}): Promise<CommentsData> => {
  const queryParams = new URLSearchParams({
    room_id: roomId,
  })

  const response = await fetchCMSApiWithStatusCode<CommentsData>(
    `${API_ENDPOINT_COMMENTS_COUNT}?${queryParams.toString()}`,
    'GET',
    null,
    {},
    '',
    isSSR,
  )

  if (response?.status !== 200) return EMPTY_COMMENTS_DATA

  return response.results as CommentsData
}

export async function checkUserVoucher(worldId: string, profileId: string) {
  const result = await fetchCMSApiWithStatusCode<{ status: number }>(
    `${API_GET_HUBS}/${worldId}/voucher-check?account_id=${profileId}`,
    'GET',
    null,
    {},
  )

  return result?.status === 200
}

export function getVoucherIds(
  htcToken: string,
  profileId: string,
  chain: string = 'digital_asset',
  vendorType: VendorType = 'vivebytes',
) {
  return fetchCMSApiWithStatusCode<EntryPassResults>(
    `${API_ACCOUNTS_V3}/${profileId}/nft/${chain}/${vendorType}/vouchers`,
    'GET',
    null,
    {},
    htcToken,
  )
}

export function getVoucherRooms(voucherId: string) {
  const queryParams = new URLSearchParams({
    voucher_id: voucherId,
  })

  return fetchCMSApiWithStatusCode<PaginatedResponse<Room>>(
    `${API_GET_VOUCHER_ROOMS_V2}?${queryParams.toString()}`,
    'GET',
    null,
    {},
  )
}

export type AccountSearchV2Params =
  | { subjectIds: string[]; communityUserIds?: never }
  | { communityUserIds: string[]; subjectIds?: never }

export type AccountSearchResponse = {
  data: AccountSearchResult[]
  total: number
}

export type AccountSearchResult = SearchAccount & {
  active_avatar: {
    id: string
    snapshot_url: string
    avatar_url: string
    head_icon_url: string
    data_type: number
    is_half_body: boolean
  }
}

export async function getAccountsSearchV2(
  params: AccountSearchV2Params,
  isSSR: boolean = typeof window === 'undefined',
) {
  const request =
    'subjectIds' in params
      ? { subject_ids: params.subjectIds }
      : { community_user_ids: params.communityUserIds }

  return fetchCMSApiWithStatusCode(
    API_POST_ACCOUNTS_SEARCH_V2,
    'POST',
    // @ts-expect-error -- helper JSON-stringifies object payloads before fetch
    request,
    {},
    '',
    isSSR,
  )
}

export const completeUserTask = async (taskId: string, htcToken: string) => {
  return fetchCMSApiWithStatusCode(
    `${API_ENDPOINT_USER_TASKS}/${taskId}/complete`,
    'POST',
    null,
    {},
    htcToken,
  )
}

export type RoomsAutocompleteResponse = {
  keywords: string[]
}

export function getRoomsAutocomplete(keyword: string, isSSR: boolean = false) {
  const queryParams = new URLSearchParams({
    q: keyword,
  })

  return fetchCMSApiWithStatusCode<RoomsAutocompleteResponse>(
    `${API_GET_ROOMS_AUTOCOMPLETE}?${queryParams.toString()}`,
    'GET',
    null,
    {},
    '',
    isSSR,
  )
}

export type RecentSearchResponse = {
  keywords: string[]
}

export function getRecentSearch(htcToken: string, isSSR: boolean = false) {
  return fetchCMSApiWithStatusCode<RecentSearchResponse>(
    API_RECENT_SEARCH_V1,
    'GET',
    null,
    {},
    htcToken,
    isSSR,
  )
}

export type RecentSearchPayload = {
  keywords: string[]
}

export function patchRecentSearch(htcToken: string, payload: RecentSearchPayload) {
  return fetchCMSApiWithStatusCode<RecentSearchResponse>(
    API_RECENT_SEARCH_V1,
    'PATCH',
    // @ts-expect-error payload is stringified inside
    payload,
    {},
    htcToken,
  )
}

export type RoomSearchRequest = {
  q: string
  limit?: number
  cursor?: string
  sort?: SearchSortOption
  device?: DeviceTypeWithAndroidIOS
  tag?: string
  genre?: string
  type?: RoomSearchFacetsType
  content_types?: ContentTypeCode[]
  locale?: string
  site_locale?: string
}

export type RoomSearchResultItem = Partial<Room> & {
  owner_name: string
  owner_community_user_id: string
  owner_viverse_pro?: boolean
  owner_verified_creator?: boolean
}

export type RoomSearchResponse<T = RoomSearchResultItem> = {
  next_cursor?: string
  results: T[]
}

export function searchRooms(params: RoomSearchRequest, htcToken?: string, isSSR: boolean = false) {
  return fetchCMSApiWithStatusCode<RoomSearchResponse>(
    API_POST_ROOMS_SEARCH,
    'POST',
    // @ts-expect-error payload is stringified inside
    params,
    {},
    htcToken,
    isSSR,
  )
}

export type RoomSearchFacetsResponse = {
  total_count: number
  genres: RoomTagWithCount[]
}

type RoomSearchFacetsType = 'video' | 'world'

type RoomSearchFacetsRequest = {
  q: string
  locale: Locale
  device?: DeviceTypeWithAndroidIOS
  genre?: string
  type?: RoomSearchFacetsType
  content_types?: ContentTypeCode[]
}

export function searchRoomFacets(
  params: RoomSearchFacetsRequest,
  htcToken?: string,
  isSSR: boolean = false,
) {
  const url = getAppendedQueryUrl(API_GET_ROOM_SEARCH_FACETS, params, isSSR)

  return fetchCMSApiWithStatusCode<RoomSearchFacetsResponse>(url, 'GET', null, {}, htcToken, isSSR)
}

export function getWatchHistory(
  htcToken: string,
  {
    timezone,
    cursor,
    limit,
    until_date,
    content_types,
  }: {
    timezone: string
    cursor?: string
    limit?: number
    until_date?: string
    content_types?: ContentTypeCode[]
  },
  isSSR = false,
): Promise<{ status: number; results: WatchHistoryResponse; headers: Headers } | void> {
  const url = getAppendedQueryUrl(
    API_ME_WATCH_HISTORY_ROOMS,
    { timezone, cursor, limit, until_date, content_types },
    isSSR,
  )

  return fetchCMSApiWithStatusCode<WatchHistoryResponse>(url, 'GET', null, {}, htcToken, isSSR)
}

export function getRecentWatchedRooms(htcToken: string, isSSR = false) {
  const url = getAppendedQueryUrl(API_ME_RECENT_WATCHED_ROOMS, { limit: 15 }, isSSR)

  return fetchCMSApiWithStatusCode<unknown>(
    url,
    'GET',
    null,
    { cache: 'no-store' },
    htcToken,
    isSSR,
  )
}

export const getPostMessageOriginsByRoomId = async (roomId: string): Promise<string[]> => {
  try {
    const result = await fetchCMSApiWithStatusCode<PostMessageOriginsResponse>(
      API_GET_ROOM_POSTMESSAGE_ORIGINS(roomId),
      'GET',
      null,
      {},
    )

    if (!result || result.status !== 200) return []
    return result.results.data.map((item) => item.origin)
  } catch {
    return []
  }
}
